import React, { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { portalAPI } from '../../services/api';
import { notify, reportLoadError } from '../../utils/notify';
import { toLocalISODate } from '../../utils/dateUtils';
import StatusBadge from '../../components/StatusBadge';

// The guard's leave: their requests and how they stand, the days used this
// year, and a new request (HR approves it in Approvals or Time &
// Attendance, as any other).

const EMPTY = { leave_type_id: '', start_date: '', end_date: '', reason: '' };
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
const FIELD_LABEL = { leave_type_id: 'Type', start_date: 'From', end_date: 'To', reason: 'Reason' };

const PortalLeave = () => {
  const { t, locale } = useLanguage();
  const [requests, setRequests] = useState(null);
  const [balance, setBalance] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const tRef = useRef(t);
  tRef.current = t;
  const year = Number(toLocalISODate().slice(0, 4));

  useEffect(() => {
    let cancelled = false;
    Promise.all([portalAPI.leave(), portalAPI.leaveBalance(year), portalAPI.leaveTypes()])
      .then(([r, b, ty]) => {
        if (cancelled) return;
        setRequests(r.data.requests);
        setBalance(b.data.balance.filter((x) => x.used_days > 0));
        setTypes(ty.data.leave_types);
      })
      .catch((error) => reportLoadError(error, tRef.current('Error loading your leave'), { main: true }));
    return () => { cancelled = true; };
  }, [version, year]);

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrors({});
      const res = await portalAPI.requestLeave({ ...form, leave_type_id: Number(form.leave_type_id) });
      notify(t('Leave requested: {days} working days. HR will let you know.', { days: res.data.days_requested }), 'success');
      setForm(null);
      setVersion((n) => n + 1);
    } catch (error) {
      const data = error.response?.data || {};
      if (data.errors) setErrors(data.errors);
      notify(data.errors ? t('Check the dates and the type') : (data.message ? t(data.message) : t('Error requesting leave')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const date = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const field = (name, input) => (
    <div className="pt-field">
      <label className="field-label" htmlFor={`leave-${name}`}>{t(FIELD_LABEL[name])}</label>
      {input}
      {errors[name] && <p className="pt-error">{t(errors[name])}</p>}
    </div>
  );

  return (
    <div className="pt-page">
      <div className="pt-title-row">
        <h1 className="pt-title">{t('My leave')}</h1>
        {!form && (
          <button type="button" className="btn btn--primary" onClick={() => setForm({ ...EMPTY, leave_type_id: types[0] ? String(types[0].id) : '' })}>
            <Plus aria-hidden="true" /> {t('Request leave')}
          </button>
        )}
      </div>

      {form && (
        <form className="pt-card pt-form" onSubmit={save}>
          {field('leave_type_id', (
            <select id="leave-leave_type_id" className="select" value={form.leave_type_id} required onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}>
              {types.map((ty) => <option key={ty.id} value={ty.id}>{ty.name}{ty.is_paid ? '' : ` (${t('unpaid')})`}</option>)}
            </select>
          ))}
          <div className="pt-form__row">
            {field('start_date', <input id="leave-start_date" className="input" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })} />)}
            {field('end_date', <input id="leave-end_date" className="input" type="date" required value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />)}
          </div>
          {field('reason', <textarea id="leave-reason" className="textarea" rows="2" maxLength={500} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />)}
          <div className="pt-actions">
            <button type="button" className="btn btn--secondary pt-big" onClick={() => { setForm(null); setErrors({}); }}>{t('Cancel')}</button>
            <button type="submit" className="btn btn--primary pt-big" disabled={saving}>{saving ? t('Sending...') : t('Send request')}</button>
          </div>
        </form>
      )}

      {balance.length > 0 && (
        <section className="pt-card">
          <h2 className="pt-card__title">{t('Days used in {year}', { year })}</h2>
          <ul className="pt-plain">
            {balance.map((b) => <li key={b.id}>{b.name}: <strong>{b.used_days}</strong></li>)}
          </ul>
        </section>
      )}

      {!requests ? <p className="pt-empty">{t('Loading...')}</p> : requests.length === 0 ? (
        <p className="pt-empty">{t('No leave requests yet')}</p>
      ) : (
        <ul className="pt-list">
          {requests.map((r) => (
            <li key={r.id} className="pt-card pt-row">
              <div>
                <div className="pt-row__title">{r.leave_type_name}</div>
                <div className="pt-muted">{date(r.start_date)}{r.end_date !== r.start_date ? ` – ${date(r.end_date)}` : ''} · {t('{days} days', { days: r.days_requested })}</div>
                {r.status === 'rejected' && r.rejection_reason && <div className="pt-muted">{r.rejection_reason}</div>}
              </div>
              <StatusBadge status={r.status}>{t(STATUS_LABEL[r.status] || r.status)}</StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PortalLeave;
